import EAIcon from "components/common/EAIcon";
import i18next from "i18next";
import { useContext } from "react";
import { Context } from "states/Store";
import useStyles from 'style/js-style/components/thirdparties/securityRating/SecurityRatingStyle';

import AwesomeDebouncePromise from "awesome-debounce-promise";
import clsx from 'clsx';
import EAButton from "components/common/EAButton";
import EAChoiceSeparator from "components/EAChoiceSeparator";
import EASelectInput from "components/inputs/EASelectInput";
import PrefixedTextField from "components/inputs/PrefixedInput";
import { isEmpty } from "lodash";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { shareSecurityRatingCompany } from "services/edge/SecurityRatingService";
import { extractSecurityRatingErrorCode, SecurityRatingConfigStatus, SecurityRatingContext } from "services/common/SecurityRatingUtils";
import { ERROR } from "services/common/Constants";

const getFormDefaultValues = (companyReference, companyScope, config) => {
	return ({
		"companyReference": companyReference || "",
		"companyScope": companyScope || [],
		"userCompanyLabel": config?.userCompanyLabel || ""
	});
};

const SecurityRatingSharableCompanies = ({
	sharableCompanies,
	onSubmit = () => { },
	isLoading = false,
	setLoading = () => { },
	goBack = () => { },
	thirdPartyId,
}) => {
	const { classes } = useStyles();
	const [state, dispatch] = useContext(Context);


	const { config, setConfig, onRiskChange } = useContext(SecurityRatingContext);

	const formMethods = useForm({
		mode: "onChange",
		delayError: 500,
		defaultValues: getFormDefaultValues(sharableCompanies?.[0]?.companyReference, sharableCompanies?.[0]?.companyScope, config)
	});

	const {
		control,
		getValues,
		handleSubmit,
		formState: { errors }
	} = formMethods;



	const handleCompanySharing = async () => {
		setLoading(true);

		const sharingDto = {
			companyReference: getValues("companyReference"),
			thirdpartyId: thirdPartyId
		}

		try {
			await shareSecurityRatingCompany(state.account.id, sharingDto, dispatch);

			setConfig(config => {
				return {
				...config,
				companyScope: getValues("companyScope"),
				userCompanyLabel: getValues("userCompanyLabel"),
				status: SecurityRatingConfigStatus.ACTIVE
			}
			});
			onRiskChange();
		} catch (error) {
			const securityRatingErrorCode = extractSecurityRatingErrorCode(error?.message?.messages?.error?.[0]?.message);
			dispatch({
				type: "ALERT",
				alert: { type: ERROR, msg: securityRatingErrorCode ? `securityRating.errors.${securityRatingErrorCode}` : "INTERNAL_ERROR" },
			});
		} finally {
			setLoading(false)
		}
	}

	const submitEvaluation = async (data) => {
		onSubmit(data);
	}

	const validateUserCompanyLabel = AwesomeDebouncePromise(async (value) => {
		return value && value?.includes("\\") ? i18next.t("securityRating.form.userCompanyLabelError") : null;
	}, 500);

	const formatSharableCompaniesOptions = (sharableCompanies) => {
		return sharableCompanies?.map(company => ({
			value: company.companyReference,
			label: company.companyName
		}))
	}

	return (
		<FormProvider {...formMethods}>
			<form
				onSubmit={handleSubmit(submitEvaluation)}
				className={classes.formWrapper}
				autoComplete="nope"
			>
				<div className={classes.sharableCompaniesContainer}>
					<div className={classes.goBack} onClick={goBack}>
						<EAIcon icon="/arrow/arrow-back-navigation" />
						{i18next.t("back")}
					</div>
					<div className={classes.sharableCompaniesBloc}>
						<div className={classes.choiceLabel}>
							{i18next.t("securityRating.form.sharableCompanies.evaluationExists", { count: sharableCompanies?.length })}
						</div>
						<div className={classes.sharingChoice}>
							<EASelectInput
								fieldName="companyReference"
								options={formatSharableCompaniesOptions(sharableCompanies)}
								translateOptions={false}
							/></div>
						<div className={classes.shouldShare}>{i18next.t("securityRating.form.sharableCompanies.shouldShare")}</div>
						<div>
							<EAButton
								type="button"
								onClick={handleCompanySharing}
								disabled={!isEmpty(errors)}
								content={
									<>
										<EAIcon icon="library_books" className={classes.evaluateBtnIcon} />
										<span>
											{i18next.t("securityRating.form.evaluate")}
										</span>
									</>
								}
								colorVariant="primary"
								size="small"
								validateLoading={isLoading}
							/>
						</div>
					</div>
					<EAChoiceSeparator />
					<div className={clsx(classes.userCompanyLabel, classes.sharableCompanyUserLabel)}>
						<div className={clsx(classes.choiceLabel)}>
							{i18next.t("securityRating.form.sharableCompanies.newEvaluation")}
						</div>
						<div>{i18next.t("securityRating.form.sharableCompanies.newEvaluationLabel")}</div>
						<div >
							<Controller
								control={control}
								name="userCompanyLabel"
								rules={{
									validate: (value) => validateUserCompanyLabel(value),
								}}
								render={({ field }) => (
									<PrefixedTextField
										id="userCompanyLabel"
										sx={{ m: 1, width: '25ch' }}
										fullWidth
										placeholder={i18next.t("securityRating.form.userCompanyLabelPlaceholder")}
										variant="outlined"
										error={!!errors.userCompanyLabel}
										helperText={!!errors.userCompanyLabel ? errors.userCompanyLabel.message : ""}
										prefix={state.thirdparty.name}
										autoComplete="nope"
										{...field}
									/>
								)}
							/>
						</div>
						<div className={classes.evaluateBtn}>
							<EAButton
								type="submit"
								disabled={!isEmpty(errors)}
								content={
									<>
										<EAIcon icon="newEnrichment" className={classes.evaluateBtnIcon} />
										<span>
											{i18next.t("securityRating.form.newEvaluation")}
										</span>
									</>
								}
								colorVariant="primaryLight"
								size="small"
								validateLoading={isLoading}
							/>
						</div>
					</div>
				</div>
			</form>
		</FormProvider>
	)
}

export default SecurityRatingSharableCompanies;